/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

import React, { CSSProperties, useMemo } from "react";
import { FC } from "react";
import { IconDisplay } from "./IconDisplay";
import { ReportPriorityIconEnum, WORKLIST_BADGE_HEIGHT } from "../../constant";
import { BigText } from "./BigText";

interface ReportPriorityDisplayProps {
  code: ReportPriorityIconEnum;
  color: string;
  name: string;
  border?: boolean;
  maxWidth?: number | string;
}

// IconDisplay pushes each priority glyph down with its own marginTop, so the glyphs end up a bit off
// the text center. Vertical shift (px) per icon to align the glyph with the text next to it.
const ICON_OFFSET_Y: Record<ReportPriorityIconEnum, number> = {
  [ReportPriorityIconEnum.HIGHEST]: -1,
  [ReportPriorityIconEnum.HIGH]: -1,
  [ReportPriorityIconEnum.MEDIUM]: 0,
  [ReportPriorityIconEnum.ROUTINE]: -0.5,
  [ReportPriorityIconEnum.LOW]: 0,
  [ReportPriorityIconEnum.LOWEST]: -1.5,
};

export const ReportPriorityDisplay: FC<ReportPriorityDisplayProps> = ({
  code,
  color,
  name,
  border = false,
  maxWidth = "unset",
}) => {
  const buildBorderStyle: CSSProperties = useMemo(() => {
    // Outline Small badge (components/Badge) with the priority color as border
    return {
      borderColor: color,
      height: WORKLIST_BADGE_HEIGHT,
      paddingTop: 0,
      paddingBottom: 0,
      gap: "0.25rem",
    };
  }, [color]);
  if (border) {
    return (
      <span
        className="badge badge-outline d-inline-flex align-items-center ml-2"
        style={buildBorderStyle}
      >
        {code && color && (
          // Smaller priority icon to fit the 22px badge
          <span className="d-inline-flex" style={{ transform: "scale(0.8)" }}>
            <IconDisplay
              noBox
              boxWidth="unset"
              boxHeight="unset"
              className="p-0"
              code={code}
              color={color}
            />
          </span>
        )}

        <BigText maxWidth={200} text={name} />
      </span>
    );
  }
  return (
    <>
      <span className=" d-flex align-items-center">
        {code && color && (
          <span
            className="d-inline-flex"
            style={{
              transform: `translateY(${ICON_OFFSET_Y[code] ?? 0}px)`,
            }}
          >
            <IconDisplay
              noBox
              boxWidth="unset"
              boxHeight="unset"
              className="p-xxs"
              code={code}
              color={color}
            />
          </span>
        )}
        <BigText text={name} maxWidth={maxWidth} />
      </span>
    </>
  );
};
